import { StyleSheet, Text, View, Image } from "@react-pdf/renderer";
import TableColPdf from "components/pdf/TableColPdf";
import BarcodePdf from "components/pdf/BarcodePdf";
import { QuoteWheelDetail, QuoteTireDetail } from "features/quotation/types";
import { formatCurrency, formatWheelRimDiamDisplay, getPriceDisplayText, isPriceLabel } from "utils";

/**
 * react-pdf/textkit only wraps at spaces; CJK strings without spaces never break.
 * Returning one glyph per syllable gives the line breaker valid split points.
 */
function pdfCjkHyphenationCallback(word: string): string[] {
  if (!word) return [];
  if (!/[\u3000-\u9fff\u3040-\u30ff\uff00-\uffef]/.test(word)) {
    return [word];
  }
  return Array.from(word);
}

/** Render price with ¥ on the left and value centered in the cell for PDF. */
function renderPriceWithYen(
  value: string,
  styleContent?: { fontSize?: number } & Record<string, unknown>,
) {
  const valueStyle = { fontSize: 7, flex: 1, textAlign: "center" as const, ...styleContent };
  const yenFontSize = typeof valueStyle.fontSize === "number" ? valueStyle.fontSize : 7;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: 2,
        paddingHorizontal: 4,
        width: "100%",
      }}
    >
      <Text style={{ fontSize: yenFontSize }}>¥</Text>
      <Text style={valueStyle}>{value}</Text>
      {/* Empty view to balance the ¥ symbol for true center alignment */}
      <View style={{ width: yenFontSize }} />
    </View>
  );
}

const styles = StyleSheet.create({
  estRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 4,
    borderBottom: "1px solid #707070",
    gap: 1,
  },
  estRowItem: {
    minHeight: 30,
  },
  textFooter: {
    fontSize: 8,
    textAlign: "right",
    wordWrap: "break-word",
  },
  wheelRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 4,
    gap: 10,
  },
  wheelTitle: {
    minHeight: 30,
    width: "50%",
    minWidth: 0,
    flexShrink: 1,
  },
  wheelInfo: {
    flex: 1,
  },
  wheelDetail: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    paddingBottom: 2,
    borderBottom: "1px solid #707070",
  },
  wheelComment: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 2,
  },
  wheelInfoText: {
    fontSize: 7,
    paddingHorizontal: 2,
    textAlign: "left",
    width: "100%",
    flexShrink: 1,
  },
  wheelInfoContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "stretch",
    minHeight: 30,
    width: "50%",
    minWidth: 0,
    flexShrink: 1,
    alignSelf: "stretch",
  },
  wheelInfoItem: {
    width: "20%",
    minWidth: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    marginBottom: 1,
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  /** Barcode row: pull up toward label (avoid lineHeight quirks on Text in react-pdf) */
  tireBarcodeBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 0,
  },
  tireBarcodeRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    marginTop: 0,
  },
});

interface TableWheelTireEstPreviewPdfProps {
  optionsPrice: number;
  wheelData?: QuoteWheelDetail;
  tireData?: QuoteTireDetail[];
}

/**
 * TableWheelTireEstPreviewPdf component renders a table with wheel and tire estimation data.
 * @param {TableWheelTireEstPreviewPdfProps} props - Component props
 * @param {number} props.optionsPrice - Options price
 * @param {QuoteWheelDetail} props.wheelData - Wheel detail information
 * @param {QuoteTireDetail[]} props.tireData - Array of tire details
 */
export default function TableWheelTireEstPreviewPdf({ optionsPrice, wheelData, tireData }: TableWheelTireEstPreviewPdfProps) {
  // Don't render if no wheel data
  if (!wheelData) {
    return null;
  }

  const wheelSizeWithHub = `${wheelData.wheel_size} ${formatWheelRimDiamDisplay(wheelData.midw_wheel_hub_diam)}Φ `;

  // Wheel price values to render with ¥ + value
  const wheelOptionVal = formatCurrency(optionsPrice || 0);
  const wheelSaleVal = formatCurrency(wheelData.sale_price || 0);
  const wheelDiscountedVal = formatCurrency(wheelData.discounted_sale_price || 0);
  const wheelTotalVal = formatCurrency(wheelData.confirm_total_price || 0);

  return (
    <View>
      <View style={styles.wheelRow}>
        <Image
          src={wheelData.image_url || "/img/wheel/wheel.png"}
          style={{ width: 60, height: 60 }}
        />
        <View style={styles.wheelInfo}>
          <View style={styles.wheelDetail}>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="ホイールメーカー"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelData.maker_name || "-"}</Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="サイズ/ハブ径"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>
                  {wheelSizeWithHub}
                </Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="本数"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelData.quantity || 0}本</Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="割引率"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>
                  {wheelData.special_discount ? `${wheelData.special_discount}%` : `${wheelData.fixed_discount || 0}%`}
                </Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="オプション合計"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                {isPriceLabel(wheelOptionVal) ? (
                  renderPriceWithYen(wheelOptionVal)
                ) : (
                  <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelOptionVal}</Text>
                )}
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="ホイールブランド"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelData.brand_name || "-"}</Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="カラー"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelData.wheel_color_name || "-"}</Text>
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="通常価格"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                {isPriceLabel(wheelSaleVal) ? (
                  renderPriceWithYen(wheelSaleVal)
                ) : (
                  <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelSaleVal}</Text>
                )}
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="割引後価格"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                {isPriceLabel(wheelDiscountedVal) ? (
                  renderPriceWithYen(wheelDiscountedVal)
                ) : (
                  <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelDiscountedVal}</Text>
                )}
              </View>
            </TableColPdf>
            <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
              styleBoxTitle={styles.wheelTitle}
              styleBox={styles.wheelInfoItem}
              title="ホイール合計"
              styleTitle={{ width: "100%", paddingLeft: 2 }}
            >
              <View style={styles.wheelInfoContent}>
                {isPriceLabel(wheelTotalVal) ? (
                  renderPriceWithYen(wheelTotalVal, { fontSize: 10 })
                ) : (
                  <Text hyphenationCallback={pdfCjkHyphenationCallback} style={styles.wheelInfoText}>{wheelTotalVal}</Text>
                )}
              </View>
            </TableColPdf>
          </View>

          <View style={styles.wheelComment}>
            {wheelData.midw_jan_cd ? (
              <View style={styles.tireBarcodeBlock}>
                <Text style={styles.textFooter}>ホイールバーコード</Text>
                <View style={styles.tireBarcodeRow}>
                  <BarcodePdf value={wheelData.midw_jan_cd} />
                </View>
              </View>
            ) : (
              <View style={{ height: 20, width: "40%" }} />
            )}
            <Text style={styles.textFooter}>備考: {wheelData.remarks || ""}</Text>
          </View>
        </View>
      </View>
      {tireData?.map((tire, index) => {
        const tireDiscountAmount =
          (tire.confirm_total_price ?? 0) + (tire.cb_tire_price ?? 0) - (
            (wheelData.confirm_total_price ?? 0) +
            (tire.discounted_sale_price ?? 0) +
            (tire.wage_cost_price ?? 0) +
            (tire.puncture_protection_price ?? 0)
          );
        const normalVal = formatCurrency(tire.nomal_price || 0);
        const discountedVal = formatCurrency(tire.discounted_sale_price || 0);
        const wageVal = formatCurrency(tire.wage_cost_price || 0);
        const punctureVal = formatCurrency(tire.puncture_protection_price || 0);
        const discountVal = tireDiscountAmount !== 0 ? formatCurrency(tireDiscountAmount) : '0';
        const totalVal = formatCurrency(tire.confirm_total_price || 0);
        const cashbackVal = formatCurrency(tire.cb_tire_price || 0);

        return (
          <View key={`tire-${tire.tire_item_cd}-${tire.pairing_number}`}>
            <View style={styles.estRow}>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 140 }}
                title="メーカー/商品名/サイズ"
                content={`${tire.maker_name || ""}\n${tire.item_name || ""}`}
                styleContent={{ textAlign: "left" }}
                styleTitle={{ textAlign: "left", width: "100%", paddingLeft: 2 }}
              />
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 26 }}
                title="LI"
                content={tire.midt_tire_loin_speed_mark || "-"}
              />
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 70 }}
                title="ラベリング"
                content={tire.tire_label || "-"}
              />
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 27 }}
                title="本数"
                content={`${tire.quantity || 0}本`}
              />
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 50 }}
                title="通常価格"
                content={!isPriceLabel(normalVal) ? normalVal : undefined}
              >
                {isPriceLabel(normalVal) ? renderPriceWithYen(normalVal) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 40 }}
                title="割引率"
                content={
                  tire.special_discount ? `${tire.special_discount}%` : `${tire.fixed_discount || 0}%`
                }
              />
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 50 }}
                title="割引後価格"
                content={!isPriceLabel(discountedVal) ? discountedVal : undefined}
              >
                {isPriceLabel(discountedVal) ? renderPriceWithYen(discountedVal) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ flex: 1 }}
                title="工賃"
                styleTitle={{ fontSize: 7 }}
                content={!isPriceLabel(wageVal) ? wageVal : undefined}
              >
                {isPriceLabel(wageVal) ? renderPriceWithYen(wageVal) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 55 }}
                title="パンク補償価格"
                content={!isPriceLabel(punctureVal) ? punctureVal : undefined}
              >
                {isPriceLabel(punctureVal) ? renderPriceWithYen(punctureVal) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ width: 55 }}
                title="値引き"
                styleTitle={{ fontSize: 7, letterSpacing: -1 }}
                content={!isPriceLabel(discountVal) ? discountVal : undefined}
              >
                {isPriceLabel(discountVal) ? renderPriceWithYen(discountVal !== '0' ? `-${discountVal}` : discountVal) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ flex: 1 }}
                title="見積金額"
                content={!isPriceLabel(totalVal) ? totalVal : undefined}
                styleContent={{ fontSize: 10 }}
              >
                {isPriceLabel(totalVal) ? renderPriceWithYen(totalVal, { fontSize: 10 }) : null}
              </TableColPdf>
              <TableColPdf hyphenationCallback={pdfCjkHyphenationCallback}
                styleBoxTitle={styles.estRowItem}
                hiddenHeader={index > 0}
                styleBox={{ flex: 1 }}
                title="キャッシュバック"
                styleTitle={{ fontSize: 10, letterSpacing: -1 }}
                content={!isPriceLabel(cashbackVal) ? cashbackVal : undefined}
              >
                {isPriceLabel(cashbackVal) ? renderPriceWithYen(cashbackVal) : null}
              </TableColPdf>
            </View>
            <View style={styles.footer}>
              {tire.midt_jan_cd ? (
                <View style={styles.tireBarcodeBlock}>
                  <Text style={styles.textFooter}>タイヤバーコード</Text>
                  <View style={styles.tireBarcodeRow}>
                    <BarcodePdf value={tire.midt_jan_cd} />
                    <Text style={styles.textFooter}>{getPriceDisplayText(
                      wheelData.discounted_sale_price ?? 0,
                      tire.discounted_sale_price ?? 0,
                    ).text}</Text>
                  </View>
                </View>
              ) : (
                <View style={{ height: 20, width: "40%" }} />
              )}
              <Text style={styles.textFooter}>備考: {tire.remarks || ""}</Text>
            </View>
          </View>
        )
      })}
    </View>
  );
}
